import React, { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { Model as PalmLowModel } from './models/plants/PalmLow.jsx'
import { Model as PalmWindModel } from './models/plants/PalmWind.jsx'
import { Model as Palm3Model } from './models/plants/Palm3.jsx'

// Animated wrapper for palm trees with wind effect
function AnimatedPalm({
  children,
  baseRotation = [0, 0, 0],
  swaySpeed = 0.5,
  swayAmplitude = 0.1,
  tiltSpeed = 0.3,
  tiltAmplitude = 0.05,
  breathSpeed = 0.8,
  breathAmplitude = 0.03,
  phase = 0
}) {
  const ref = useRef()

  useFrame((state) => {
    if (ref.current) {
      const time = state.clock.getElapsedTime() + phase

      // Sway around Y axis
      ref.current.rotation.y = baseRotation[1] + Math.sin(time * swaySpeed) * swayAmplitude

      // Slight tilt in X and Z
      ref.current.rotation.x = baseRotation[0] + Math.sin(time * tiltSpeed) * tiltAmplitude
      ref.current.rotation.z = baseRotation[2] + Math.cos(time * tiltSpeed * 1.3) * tiltAmplitude

      // Subtle "breathing" scale
      const breathingScale = 1 + Math.sin(time * breathSpeed) * breathAmplitude
      ref.current.scale.setScalar(breathingScale)
    }
  })

  return (
    <group ref={ref} frustumCulled={false}>
      {children}
    </group>
  )
}

export function AnimatedPalmLow(props) {
  return (
    <AnimatedPalm
      baseRotation={[0, 0, 0]}
      swaySpeed={0.3}
      swayAmplitude={0.03}
      tiltSpeed={0.2}
      tiltAmplitude={0.015}
      breathSpeed={0.5}
      breathAmplitude={0.008}
      phase={0}
    >
      <PalmLowModel {...props} frustumCulled={false} />
    </AnimatedPalm>
  )
}

export function AnimatedPalmWind1(props) {
  return (
    <AnimatedPalm
      baseRotation={[0, 0, 0]}
      swaySpeed={0.35}
      swayAmplitude={0.02}
      tiltSpeed={0.03}
      tiltAmplitude={0.02}
      breathSpeed={0.2}
      breathAmplitude={0.01}
      phase={Math.PI * 0.5}
    >
      <PalmWindModel {...props} frustumCulled={false} />
    </AnimatedPalm>
  )
}

export function AnimatedPalmWind2(props) {
  const { rotation = [0, 0, 0] } = props
  return (
    <AnimatedPalm
      baseRotation={rotation}
      swaySpeed={0.3}
      swayAmplitude={0.035}
      tiltSpeed={0.22}
      tiltAmplitude={0.018}
      breathSpeed={0.55}
      breathAmplitude={0.009}
      phase={Math.PI * 1.2}
    >
      <PalmWindModel {...props} frustumCulled={false} />
    </AnimatedPalm>
  )
}

export function AnimatedPalm3(props) {
  return (
    <AnimatedPalm
      baseRotation={[0, 0, 0]}
      swaySpeed={0.2}        // Slower sway
      swayAmplitude={0.008}  // Very minimal Y-axis rotation
      tiltSpeed={0.15}       // Slower tilt
      tiltAmplitude={0.005}  // Tiny X/Z tilt - upper parts move, base stable
      breathSpeed={0.4}      // Slower breathing
      breathAmplitude={0.003} // Almost imperceptible scale change
      phase={Math.PI * 0.8}
    >
      <Palm3Model {...props} frustumCulled={false} />
    </AnimatedPalm>
  )
}